import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CsPage, NavController } from 'providers/nav-controller';
import { AuthRole } from '@cf-shared/couch-auth';
import { AuthService } from 'services/auth.service';
import { DateTime } from 'luxon';
import { DebugService } from 'services/debug.service';
import { LoggingService } from 'services/logging.service';
import { DatabaseRecoveryService } from 'services/database-recovery.service';
import { PubSubService } from 'services/pubsub.service';
import { DayUpdatedEvent } from 'services/database-wrapper';
import { SettingsService } from 'services/settings.service';
import { IInputCalendarPage } from 'pages/input-calendar-page/input-calendar-interface';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { untranslatedString } from 'models/translated-string';
import { ITestWorkoutSolverPage } from 'pages/test-workout-solver/test-workout-solver-interface';
import { IIntroCarouselPage } from 'pages/intro-carousel-page/intro-carousel-interface';
import { ITestNavigationRootPage } from 'pages/test-navigation-root/test-navigation-root-interface';
import { ITestUiComponentsPage } from 'pages/test-ui-components/test-ui-components-interface';
import { FoodDbService } from 'services/food-db.service';
import { LoadingController } from 'providers/loading-controller';
import { IDbSnapshotsPage } from 'pages/db-snapshots/db-snapshots-interface';
import { IDbRestoreJsonPage } from 'pages/db-restore-json/db-restore-json-interface';
import { NotificationService } from 'services/notification.service';
import { UserDb } from 'services/user-db.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { DatabaseWrapper } from 'services/database-wrapper';
import { CoachDemoSeedService } from 'services/coach-demo-seed.service';
import { ICoachSettingsPage } from 'pages/coach-settings/coach-settings-interface';

@Component({
    selector: 'page-testing-root',
    templateUrl: 'testing-root.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TestingRootPage extends CsPage<Record<string, never>> {
  // Page interfaces for template navigation
  ITestWorkoutSolverPage = ITestWorkoutSolverPage;
  IIntroCarouselPage = IIntroCarouselPage;
  ITestNavigationRootPage = ITestNavigationRootPage;
  ITestUiComponentsPage = ITestUiComponentsPage;
  IDbSnapshotsPage = IDbSnapshotsPage;
  IDbRestoreJsonPage = IDbRestoreJsonPage;

  constructor(
    public navCtrl: NavController,
    protected auth: AuthService,
    private debug: DebugService,
    private pubSub: PubSubService,
    public logging: LoggingService,
    public databaseRecovery: DatabaseRecoveryService,
    private alertCtrl: AlertController,
    public settings: SettingsService,
    private foodDb: FoodDbService,
    private loadingCtrl: LoadingController,
    private notificationService: NotificationService,
    private userDb: UserDb,
    private cloudFunctions: CloudFunctionsService,
    private demoSeed: CoachDemoSeedService,
  ) {
    super();
  }

  async ionViewCanEnter() {
    await this.auth.pageAuthGuardAsync(AuthRole.Unknown);
  }

  async handleSetFakeNow(_ev: Event) {
    const dateISO = this.debug.getNowLocal().toISODate();
    const result = await this.navCtrl.callAsync(IInputCalendarPage, { dateISO });
    if (result?.dateISO) {
      const realNow = DateTime.local();
      const newNow = DateTime.fromISO(result.dateISO).set({ hour: realNow.hour, minute: realNow.minute });
      await this.debug.setNowLocalAsync(newNow);
      console.log('Fake now set to: ', newNow);
      // this is needed to update view on home page if returning from this page
      this.pubSub.publish(DayUpdatedEvent, {});

      await this.alertCtrl.showAlertAsync({
        message: untranslatedString(`Fake now set to: ${newNow}`),
        buttons: [
          {
            text: untranslatedString('Ok'),
            role: AlertButtonRole.Primary,
          },
        ],
      });
    }
  }

  async handleUnsetFakeNow() {
    await this.debug.setNowLocalAsync(null);
    console.log('Fake now unset.');
    // this is needed to update view on home page if returning from this page
    this.pubSub.publish(DayUpdatedEvent, {});

    await this.alertCtrl.showAlertAsync({
      message: untranslatedString('Fake now unset.'),
      buttons: [
        {
          text: untranslatedString('Ok'),
          role: AlertButtonRole.Primary,
        },
      ],
    });
  }

  handleFakeCrash() {
    void (async () => {
      console.error('Testing crash reporting!');
      throw new Error('test Crash!');
    })();
  }
  handleFakeError() {
    this.logging.trackAbnormalError('Testing error reporting!', untranslatedString('Testing error reporting!'));
  }

  handleFakeException() {
    this.logging.trackAbnormalException(new Error("test exception"), untranslatedString('Testing exception reporting!'));
  }
  handleFakeSilentProblem() {
    this.logging.trackSilentProblem('Testing silent problem reporting!', 'Silent context');
  }

  handleFakeFatalError() {
    void this.logging.fatalError({ type: 'test_fatal_error' });
  }

  handleFakeDatabaseError() {
    const error = new Error('Testing database recovery');
    error.name = 'indexed_db_went_bad';
    this.databaseRecovery.recoverFromDatabaseError(error);
  }

  async handleTestNotification() {
    await this.notificationService.scheduleTestNotificationAsync(
      this.userDb, this.settings, this.debug.getNowLocal(), this.cloudFunctions,
    );
    await this.alertCtrl.showAlertAsync({
      message: untranslatedString('Test push scheduled. It will arrive on all mobile devices within the next minute.'),
      buttons: [{ text: untranslatedString('Ok'), role: AlertButtonRole.Primary }],
    });
  }

  async handleUncacheAllFoods() {
    const loading = this.loadingCtrl.create({ content: untranslatedString('Uncaching all foods...') });
    void loading.present();
    await this.foodDb.uncacheAllFoodsAsync();
    void loading.dismiss();

    const shouldReload = await this.alertCtrl.showAlertAsync<boolean>({
      message: untranslatedString('All foods uncached. Please reload the app.'),
      buttons: [
        {
          text: untranslatedString('Reload'),
          role: AlertButtonRole.Primary,
          result: true,
        },
      ],
    });

    if (shouldReload) {
      window.location.reload();
    }
  }

  /**
   * Put the running app into "demo coach" mode for visual QA of
   * `page-coach-settings` without an E2E run. What gets installed, and
   * why a repeat tap is safe, is described on
   * `CoachDemoSeedService.installAsync`.
   *
   * Slot documents persist across reloads; the in-memory seed does
   * not, so after a reload the synthetic rows fall through to a
   * remote-fetch attempt and resolve as Unknown — that is the
   * expected fallback for a stale demo. Use "Remove demo coach data"
   * for a clean teardown.
   */
  async handleBecomeDemoCoach() {
    if (!this.auth.couch) {
      await this.alertCtrl.showAlertAsync({
        message: untranslatedString(
          'Cannot enter demo coach mode: no CouchDB session. Sign in first.'),
        buttons: [{ text: untranslatedString('Ok'), role: AlertButtonRole.Primary }],
      });
      return;
    }

    const { legacyDeleted, written, keptExisting } = await this.demoSeed.installAsync(
      this.userDb.database, this.auth.couch, DatabaseWrapper.getTransactionId());

    const message = legacyDeleted > 0
      ? `Demo coach mode reinstalled: removed ${legacyDeleted} legacy demo slot${legacyDeleted === 1 ? '' : 's'}, wrote ${written} fresh, kept ${keptExisting} existing (coachMaxSlots = 5). Open Coaching settings now?`
      : written > 0
        ? `Demo coach mode installed: coachMaxSlots = 5, ${written} synthetic slots saved. Open Coaching settings now?`
        : 'Demo coach mode already installed (in-memory seed refreshed, no slots changed). Open Coaching settings now?';
    const shouldOpen = await this.alertCtrl.showAlertAsync<boolean>({
      message: untranslatedString(message),
      buttons: [
        { text: untranslatedString('Stay'), role: AlertButtonRole.Cancel, result: false },
        { text: untranslatedString('Open'), role: AlertButtonRole.Primary, result: true },
      ],
    });
    if (shouldOpen) {
      await this.navCtrl.pushAsync(ICoachSettingsPage);
    }
  }

  /**
   * Tear down the demo coach state installed by `handleBecomeDemoCoach`.
   * See `CoachDemoSeedService.removeAsync`.
   */
  async handleRemoveDemoCoach() {
    const removed = await this.demoSeed.removeAsync(
      this.userDb.database, this.auth.couch, DatabaseWrapper.getTransactionId());

    await this.alertCtrl.showAlertAsync({
      message: untranslatedString(
        `Removed ${removed} demo slot${removed === 1 ? '' : 's'} and cleared the in-memory seed.`),
      buttons: [{ text: untranslatedString('Ok'), role: AlertButtonRole.Primary }],
    });
  }
}
